import React, { useState, useMemo } from "react";
import { Star, Search, LayoutGrid } from "lucide-react";
import { CardItem, API_CATEGORIES, INITIAL_CARDS } from "../data";

interface ApiMarketProps {
  cards?: CardItem[];
  onToggleStar?: (id: string) => void;
  title?: string;
}

export default function ApiMarket({ cards = INITIAL_CARDS, onToggleStar, title = "API 集市" }: ApiMarketProps) {
  const [activeTab, setActiveTab] = useState<'products' | 'starred'>('products');
  const [activeSubCategory, setActiveSubCategory] = useState<string | null>(null);
  const [searchKey, setSearchKey] = useState("");
  
  // 如果没有提供 onToggleStar 回调，提供一个空函数
  const handleToggleStar = onToggleStar || (() => {});

  const iconMap: any = {
    patent: <LayoutGrid className="w-4 h-4" />,
    trademark: <LayoutGrid className="w-4 h-4" />,
    enterprise: <LayoutGrid className="w-4 h-4" />,
    agency: <LayoutGrid className="w-4 h-4" />,
    analysis: <LayoutGrid className="w-4 h-4" />,
    copyright: <LayoutGrid className="w-4 h-4" />,
    standard: <LayoutGrid className="w-4 h-4" />,
  };

  // 根据当前标签和筛选条件过滤卡片
  const filteredCards = useMemo(() => {
    return cards.filter(card => {
      const matchesTab = activeTab === 'starred' ? card.isStarred : true;
      const matchesSub = activeSubCategory ? card.subCategory === activeSubCategory : true;
      const matchesSearch = card.name.includes(searchKey) || card.description.includes(searchKey);
      return matchesTab && matchesSub && matchesSearch;
    });
  }, [cards, activeTab, activeSubCategory, searchKey]);

  // 获取当前显示的分类
  const categoriesToShow = useMemo(() => {
    if (activeTab === 'starred') {
      const starredCategories = new Set(filteredCards.map(c => c.category));
      return API_CATEGORIES.filter(cat => starredCategories.has(cat.id));
    }
    return API_CATEGORIES;
  }, [activeTab, filteredCards]);

  return (
    <div className="flex flex-col bg-white h-full">
      {/* 顶部搜索和标题 */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-50 bg-white sticky top-0 z-10">
        <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input 
            type="text" 
            placeholder="搜索关键字" 
            className="pl-9 pr-4 py-1.5 bg-gray-50 border border-gray-200 rounded text-sm w-64 focus:outline-none focus:ring-1 focus:ring-blue-500"
            value={searchKey}
            onChange={(e) => setSearchKey(e.target.value)}
          />
        </div>
      </div>

      {/* 标签切换 */}
      <div className="flex gap-6 px-6 py-4 border-b border-gray-50 bg-white">
        <button 
          className={`text-sm pb-1 transition-all relative ${activeTab === 'products' ? 'text-blue-600 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => { setActiveTab('products'); setActiveSubCategory(null); }}
        >
          产品类目
          {activeTab === 'products' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />}
        </button>
        <button 
          className={`text-sm pb-1 transition-all relative ${activeTab === 'starred' ? 'text-blue-600 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => { setActiveTab('starred'); setActiveSubCategory(null); }}
        >
          重点关注
          {activeTab === 'starred' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />}
        </button>
      </div>

      {/* 主要内容区域 - 共享滚动条 */}
      <div className="flex-1 overflow-auto p-6 bg-gray-50/30">
        <div className="flex mx-auto">
          {/* 左侧筛选区域 - 不单独滚动 */}
          <div className="w-64 pr-6 border-r border-gray-100 shrink-0">
            <div className="space-y-6">
              {categoriesToShow.map(cat => (
                <div key={cat.id} className="space-y-3">
                  <div className="flex items-center gap-2 text-gray-800 font-semibold text-sm">
                    <span className="p-1 rounded bg-gray-50 text-gray-600">{iconMap[cat.id]}</span>
                    <span>{cat.name}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 ml-1">
                    {cat.items.filter(item => {
                      if (activeTab === 'starred') {
                        return filteredCards.some(c => c.subCategory === item);
                      }
                      return true;
                    }).map(item => (
                      <button 
                        key={item}
                        onClick={() => setActiveSubCategory(activeSubCategory === item ? null : item)}
                        className={`px-3 py-1 text-xs rounded border transition-all ${
                          activeSubCategory === item 
                            ? 'bg-blue-50 border-blue-200 text-blue-600 font-medium shadow-sm' 
                            : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300 hover:text-blue-500'
                        }`}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 右侧卡片网格区域 */}
          <div className="flex-1">
            <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredCards.map(card => (
                <div key={card.id} className="bg-white p-5 rounded border border-gray-100 shadow-sm hover:shadow-md transition-all group relative flex flex-col h-full min-h-[160px]">
                  <div className="flex justify-between items-start mb-4">
                    <h4 className="font-bold text-gray-800 group-hover:text-blue-600 transition-colors text-base truncate pr-8">{card.name}</h4>
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleToggleStar(card.id); }}
                      className="absolute top-5 right-5 p-1 hover:bg-gray-50 rounded-full transition-colors"
                    >
                      <Star 
                        className={`w-4 h-4 transition-all ${card.isStarred ? 'fill-blue-600 text-blue-600 scale-110' : 'text-gray-300 hover:text-gray-400'}`}
                      />
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 leading-relaxed line-clamp-3 mb-4 flex-1">
                    {card.description}
                  </p>
                </div>
              ))}
            </div>

            {filteredCards.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center py-20 text-gray-400">
                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4 text-gray-300">
                  <LayoutGrid className="w-8 h-8" />
                </div>
                <p className="text-sm font-medium">暂无相关数据</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}